/**
 * Models on your cards: up to MAX_SELECTED_MODELS ready models the user may use, ordered
 * (the first rates the photo strip and bar). Saved through preferences.
 */
import { useDealModels } from '../../api/queries'
import { HOME_BUYER_MODEL_ID } from '../../experiences/registry'
import { PickerRow, type Option } from '../../ui/kit'

export function ModelsPicker({ selectedIds, max, saving, error, onChange }: { selectedIds: string[]; max: number; saving: boolean; error: string | null; onChange: (ids: string[]) => void }) {
  const models = useDealModels()
  const visible = (models.data?.items ?? []).filter(m => m.logicalModelId !== HOME_BUYER_MODEL_ID)
  const usable = visible.filter(m => m.state === 'ready' && m.capabilities.read && m.capabilities.use)
  const options: Option[] = [
    ...selectedIds.map(id => { const m = visible.find(x => x.logicalModelId === id); const ok = usable.some(x => x.logicalModelId === id); return { value: id, label: m?.name ?? 'Unavailable model', note: ok ? (m?.sourceKind === 'system' ? 'Shared built-in' : 'Private model') : 'Unavailable · remove to change selection' } }),
    ...usable.filter(m => !selectedIds.includes(m.logicalModelId)).map(m => ({ value: m.logicalModelId, label: m.name, note: m.sourceKind === 'system' ? 'Shared built-in' : 'Private model' })),
  ]
  const names = selectedIds.map(id => visible.find(m => m.logicalModelId === id)?.name ?? '?')
  // Keep the toolbar field short: two names, then a count.
  const joined = names.join(' · ')
  const summary = names.length === 0 ? 'None' : names.length === 1 || joined.length <= 26 ? joined : `${names[0]} +${names.length - 1}`
  return (
    <PickerRow
      variant="tool" label="Models" options={options} value={selectedIds} multi max={max} orderable clearable={false} busy={saving}
      summaryOverride={models.isPending ? 'Loading…' : `${summary} (${selectedIds.length}/${max})`}
      title={`Choose up to ${max === 3 ? 'three' : max} models. Kill applies only when every selected model rejects.`}
      onChange={v => onChange(v as string[])}
      footer={<>
        <span className="t-muted">Choose up to {max === 3 ? 'three' : max} models; every selected model is shown on the card and the first rates the photo. Kill applies only when every selected model rejects.</span>
        {models.isError ? <span className="t-muted">{models.error.message}</span> : null}
        {selectedIds.length === 0 ? <span className="t-muted">No models selected. Cards remain unresolved.</span> : null}
        {error ? <span className="fld-error">Selection could not be saved: {error}</span> : null}
      </>}
    />
  )
}
